import { useState, useEffect, useRef } from 'react';
import { getValidToken } from '../auth/tokenManager';
import { getShiftStreamName } from '../utils/tokenUtils';
import { getActiveNatsUser, getActiveOrgAlias } from '../services/demoMode';
import type { WorkerToMainMsg, MainToWorkerMsg } from '../workers/liveShift.worker';
import LiveShiftWorker from '../workers/liveShift.worker?worker';

export interface ShiftInfo {
  designation: string;
  date: string;
  start: string;
  end: string;
}

export interface ShiftKPIs {
  target: number | null;
  good_products: number | null;
  waste: number | null;
  completion_pct: number | null;
  utilization_ratio: number | null;
  machine_efficiency: number | null;
  current_speed: number | null;
  nominal_speed: number | null;
  mtbf_seconds: number | null;
  external_stops: number | null;
}

export interface TopStatus {
  code: number;
  name: string;
  duration_seconds: number;
  quantity: number;
}

export interface TopReject {
  reject_no: number;
  cause: string;
  quantity: number;
}

export interface TopStatusData {
  serial: string;
  shift: ShiftInfo;
  items: TopStatus[];
  timestamp: string;
}

export interface TopRejectData {
  serial: string;
  shift: ShiftInfo;
  items: TopReject[];
  timestamp: string;
}

export interface StatusTransition {
  from: number;
  to: number;
  from_name: string;
  to_name: string;
  at: string;
}

export interface LiveShiftData {
  serial: string;
  shift: ShiftInfo;
  kpis: ShiftKPIs;
  timestamp: string;
}

export interface LiveStatusTransition {
  serial: string;
  shift: ShiftInfo;
  transition: StatusTransition;
  timestamp: string;
}

export function useLiveShiftData(machineId: string | undefined) {
  const [shiftData, setShiftData] = useState<LiveShiftData | null>(null);
  const [lastTransition, setLastTransition] = useState<LiveStatusTransition | null>(null);
  const [topStops, setTopStops] = useState<TopStatusData | null>(null);
  const [topRejects, setTopRejects] = useState<TopRejectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasStream, setHasStream] = useState(false);
  // Recovery indicator. `true` once the worker reports a live connection and
  // flips to `false` during the stale/reconnect window (STATUS { connected:false }).
  // It does NOT blank the view — last-known shiftData/topStops/topRejects/
  // lastTransition are intentionally preserved so the view keeps rendering the
  // most recent values until fresh data arrives after re-subscribe.
  const [connected, setConnected] = useState(false);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    if (!machineId) {
      setLoading(false);
      setHasStream(false);
      setConnected(false);
      return;
    }

    let cancelled = false;

    const start = async () => {
      try {
        const token = await getValidToken();
        if (cancelled) return;
        const orgAlias = getActiveOrgAlias(token);
        if (!orgAlias) {
          setLoading(false);
          return;
        }

        // Live Shift data is sourced into the dedicated <ORG>_SHIFT stream.
        const streamName = getShiftStreamName(orgAlias);
        const serial = machineId.padStart(5, '0');
        const serverUrl = import.meta.env.VITE_NATS_URL || 'wss://natsws.enlixy.com:443';

        // Spawn worker
        const worker = new LiveShiftWorker();
        workerRef.current = worker;

        worker.onmessage = (event: MessageEvent<WorkerToMainMsg>) => {
          if (cancelled) return;
          const msg = event.data;
          switch (msg.type) {
            case 'SHIFT_DATA':
              setShiftData(msg.data as LiveShiftData);
              setLoading(false);
              break;
            case 'TRANSITION_DATA':
              setLastTransition(msg.data as LiveStatusTransition);
              break;
            case 'TOP_STATUS_DATA':
              console.log('[useLiveShiftData] TOP_STATUS_DATA received:', msg.data);
              setTopStops(msg.data as TopStatusData);
              break;
            case 'TOP_REJECT_DATA':
              console.log('[useLiveShiftData] TOP_REJECT_DATA received:', msg.data);
              setTopRejects(msg.data as TopRejectData);
              break;
            case 'STREAM_CHECK':
              setHasStream(msg.hasStream);
              if (!msg.hasStream) setLoading(false);
              break;
            case 'TOKEN_REQUEST': {
              // The worker (no Keycloak access) needs a fresh JWT before it can
              // reconnect/recover. Fetch one and post it back. Failure to refresh
              // (e.g. session truly expired) falls through to login via
              // getValidToken's own handling.
              void (async () => {
                try {
                  const freshToken = await getValidToken();
                  workerRef.current?.postMessage({
                    type: 'TOKEN_REFRESH',
                    token: freshToken,
                  } as MainToWorkerMsg);
                } catch (tokenErr) {
                  console.warn('[useLiveShiftData] Token refresh for worker failed:', tokenErr);
                }
              })();
              break;
            }
            case 'STATUS':
              // Recovery handling (Fix B): the worker posts STATUS to signal
              // connection lifecycle. We surface it via `connected` but MUST NOT
              // blank the view during a stale/reconnect window.
              if (msg.connected) {
                // Re-subscribed / live. Fresh data follows on its own messages.
                setConnected(true);
              } else if (msg.error) {
                // Hard failure (stream not found / recovery failed): stop the
                // initial spinner, but keep any last-known values rendered.
                console.warn('[useLiveShiftData] Worker connection error:', msg.error);
                setConnected(false);
                setLoading(false);
              } else {
                // Transient stale/reconnect window (no error): keep last-known
                // values — do NOT clear shiftData/topStops/topRejects/
                // lastTransition and do NOT flip hasStream/loading.
                setConnected(false);
              }
              break;
          }
        };

        const connectMsg: MainToWorkerMsg = {
          type: 'CONNECT',
          serverUrl,
          token,
          streamName,
          machineSerial: serial,
          natsUser: getActiveNatsUser(),
        };
        worker.postMessage(connectMsg);
      } catch (err) {
        console.warn('[useLiveShiftData] Setup error:', err);
        if (!cancelled) setLoading(false);
      }
    };

    start();

    // Proactively forward Keycloak token refreshes to the worker so its own
    // NATS connection (and nats.ws internal auto-reconnect) always has a fresh
    // token. Without this, after the access token expires the worker's socket
    // goes stale and reconnect attempts loop on AUTHORIZATION_VIOLATION until a
    // recovery happens to request a new token.
    const handleTokenRefreshed = (e: Event) => {
      const token = (e as CustomEvent<{ token?: string }>).detail?.token;
      if (token && workerRef.current) {
        workerRef.current.postMessage({ type: 'TOKEN_REFRESH', token } as MainToWorkerMsg);
      }
    };
    window.addEventListener('keycloak-token-refreshed', handleTokenRefreshed);

    return () => {
      cancelled = true;
      window.removeEventListener('keycloak-token-refreshed', handleTokenRefreshed);
      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'DISCONNECT' } as MainToWorkerMsg);
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [machineId]);

  return { shiftData, lastTransition, topStops, topRejects, loading, hasStream, connected };
}
